import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Minus, Plus, ArrowRight, ArrowLeft, Lock, Users, Check,
  ShieldCheck, Heart, AlertTriangle,
} from "lucide-react";
import { PUBLIC_API, apiClient } from "../lib/api";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import PublicSiteHeader from "../components/PublicSiteHeader";
import IdentityVerification from "../components/IdentityVerification";
import AddressAutocomplete from "../components/shared/AddressAutocomplete";
import OnVousConnait from "../components/reconnaissance/OnVousConnait";
import { chargerFicheProuvee, nomModifie, nomsFiges } from "../lib/ficheProuvee";
import { estManquant, phrasesChampsManquants } from "../lib/champsManquants";
import { messageErreur } from "../lib/messageErreur";

/**
 * Page publique d'une FÊTE (Kippour & co.) — /fete/:pageId
 *
 * Un lien à diffuser (WhatsApp), plusieurs offres sur un écran, et un
 * parcours court : Offres → (suggestions) → Identité → paiement Terouma.
 *
 * Le paiement lui-même n'est PAS refait ici : la commande crée un engagement
 * par offre (avec son motif de fête) puis renvoie vers `/pay/{token}`, la
 * page de paiement historique — donc particulier/société, couverture des
 * frais, échéancier, prélèvement automatique (opt-in explicite) et reçu
 * Cerfa fonctionnent exactement comme partout ailleurs.
 *
 * Design : clair par défaut (ivoire/or/navy) — public adultes et seniors.
 */

const IVORY = "#FBF8F1";
// Couleur d'accent = couleur de la fête de la page (--accent-rgb posée sur la
// racine) ; doré par défaut. rgba(var(...)) plutôt que color-mix : lisible
// aussi par les vieux iPhone des donateurs.
const GOLD = "rgb(var(--accent-rgb, 184,150,46))";
const GOLD_LIGHT = "rgba(var(--accent-rgb, 184,150,46), 0.7)";
const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};
const NAVY = "#0D1B33";

const eur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR",
    maximumFractionDigits: Number.isInteger(Number(v)) ? 0 : 2 }).format(Number(v || 0));

export default function FestivalPublicPage() {
  const { pageId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);          // 1 offres · 2 suggestions · 3 identité
  const [qty, setQty] = useState({});           // offer_id → quantité
  const [custom, setCustom] = useState({});     // offer_id → montant libre
  const [submitting, setSubmitting] = useState(false);
  const [prevStep, setPrevStep] = useState(1);   // pour un retour fidèle

  // Identité
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [donorType, setDonorType] = useState("particulier");
  const [companyName, setCompanyName] = useState("");
  const [siren, setSiren] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [recognized, setRecognized] = useState(null);
  // Reconnaissance SANS nom (décision d'Avi, 30/09/2026) : le code est
  // facultatif (« Remplir mes informations »), sauf abonnement mensuel ou
  // société. Après le code, la fiche revient pré-remplie et modifiable.
  const [codeOuvert, setCodeOuvert] = useState(false);
  const [ficheProuvee, setFicheProuvee] = useState(null);
  // Parcours progressif : UN champ « email ou téléphone » d'abord. Reconnu →
  // vérification d'identité puis les infos apparaissent ; inconnu → les
  // champs s'ouvrent pour créer le profil.
  const [contact, setContact] = useState("");
  const [identityStage, setIdentityStage] = useState("gate"); // gate | known | form
  const [checking, setChecking] = useState(false);
  // Contact qui a ANCRÉ la vérification : le modifier annule le jeton (le
  // serveur refuserait la commande) ; modifier les autres champs ne coûte rien.
  const [verifiedAnchors, setVerifiedAnchors] = useState([]);
  const topRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${PUBLIC_API}/festival-pages/public/${pageId}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setError(messageErreur(e, "Page introuvable."));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageId]);

  useEffect(() => { topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [step]);

  const page = data?.page;
  const org = data?.organization;
  const offers = useMemo(() => page?.offers || [], [page]);

  // Reconnaissance depuis le champ unique — email OU téléphone (même service
  // que les autres pages). Reconnu → étape « vérifier son identité » ;
  // inconnu → on ouvre les champs (nouveau profil). Une panne de la
  // reconnaissance ne bloque JAMAIS le don : on ouvre les champs.
  // Email complet (avec extension) ou numéro d'au moins 10 chiffres — en
  // dessous, on est sûrement en pleine frappe : on ne bascule pas.
  const gateContact = contact.trim();
  const gateIsEmail = /^\S+@\S+\.\S{2,}$/.test(gateContact);
  const gateIsPhone = !gateContact.includes("@")
    && gateContact.replace(/\D/g, "").length >= 10;

  useEffect(() => {
    if (identityStage !== "gate") return;
    const v = gateContact;
    const isEmail = gateIsEmail;
    if (!org?.id || (!isEmail && !gateIsPhone)) { setChecking(false); return; }
    setChecking(true);
    // `stale` : une réponse arrivée APRÈS qu'on a quitté la porte (ou changé
    // la saisie) ne doit plus toucher l'état — sinon elle écrase la suite.
    let stale = false;
    const timer = setTimeout(async () => {
      let found = null;
      try {
        const r = await axios.get(`${PUBLIC_API}/campaigns/public/recognize-donor`, {
          params: isEmail ? { org_id: org.id, email: v } : { org_id: org.id, phone: v },
        });
        if (r.data?.found) found = r.data;
      } catch { /* traité comme inconnu */ }
      if (stale) return;
      if (isEmail) setEmail(v); else setPhone(v);
      if (found) {
        setRecognized({ ...found, contact: v, method: isEmail ? "email" : "phone" });
        setIdentityStage("known");
      } else {
        setRecognized(null);
        setIdentityStage("form");
      }
      setChecking(false);
    }, 800);
    return () => { stale = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, identityStage, org?.id]);

  // Identité VÉRIFIÉE → on relance la reconnaissance avec le jeton : le
  // serveur livre alors les coordonnées complètes (email, téléphone,
  // adresse…) et le donateur peut les VÉRIFIER/corriger avant de payer.
  useEffect(() => {
    if (!verificationToken || !org?.id) return;
    const contactVal = (recognized?.contact || "").trim();
    if (!contactVal) return; // nouveau donateur : rien à récupérer
    let annule = false;
    (async () => {
      try {
        const d = await chargerFicheProuvee({
          orgId: org.id, methode: recognized?.method, contact: contactVal,
          jeton: verificationToken,
        });
        if (annule || !d) return;
        setFicheProuvee(d);
        if (d.first_name) setFirstName(d.first_name);
        if (d.last_name) setLastName(d.last_name);
        // Un email ou un téléphone DÉJÀ saisi n'est jamais remplacé par celui
        // de la fiche (relecture du 01/10/2026) : reconnu par le téléphone de
        // la maison, on garde SON email — le serveur crée sa propre fiche.
        setEmail((actuel) => (d.email && !actuel.trim() ? d.email : actuel));
        setPhone((actuel) => (d.phone && !actuel.trim() ? d.phone : actuel));
        if (d.address) setAddress(d.address);
        if (d.postal_code) setPostalCode(d.postal_code);
        if (d.city) setCity(d.city);
        if (d.company_name && !companyName) setCompanyName(d.company_name);
        if (d.siren && !siren) setSiren(d.siren);
      } catch { /* pas bloquant */ }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationToken]);

  // Même logique de comparaison que le serveur : emails insensibles à la
  // casse, téléphones comparés sur leurs 9 derniers chiffres (06… vs +336…).
  const contactsLook = (a, b) => {
    const va = (a || "").trim().toLowerCase();
    const vb = (b || "").trim().toLowerCase();
    if (!va || !vb) return false;
    if (va === vb) return true;
    if (va.includes("@") || vb.includes("@")) return false;
    const da = va.replace(/\D/g, "");
    const db = vb.replace(/\D/g, "");
    return da.length >= 9 && db.length >= 9 && da.slice(-9) === db.slice(-9);
  };

  // « Ce n'est pas moi » / « Changer d'email ou de numéro » : retour complet
  // à la porte d'entrée — autre personne, on repart de zéro (le panier reste).
  const resetIdentity = () => {
    setContact("");
    setIdentityStage("gate");
    setRecognized(null);
    setVerificationToken("");
    setVerifiedAnchors([]);
    setCodeOuvert(false);
    setFicheProuvee(null);
    setFirstName(""); setLastName(""); setEmail(""); setPhone("");
    setAddress(""); setPostalCode(""); setCity("");
    setCompanyName(""); setSiren("");
  };

  // Retour à la porte d'entrée SANS tout perdre : pour un nouveau venu qui a
  // mal tapé son numéro/email, on ne vide que le contact — son nom, son
  // adresse et sa sélection restent. (Identité reconnue/vérifiée → resetIdentity.)
  const backToGate = () => {
    if (recognized || verificationToken) { resetIdentity(); return; }
    setContact("");
    setEmail(""); setPhone("");
    setIdentityStage("gate");
  };

  const onVerifiedIdentity = (tok, ch) => {
    setVerificationToken(tok);
    // L'ancre = le contact prouvé côté serveur (contact reconnu, sinon le
    // champ correspondant au canal choisi).
    if (recognized?.contact) setVerifiedAnchors([recognized.contact]);
    else setVerifiedAnchors([ch === "sms" || ch === "whatsapp" ? phone : email]);
    setIdentityStage("form");
  };

  // Modifier le champ qui a servi de preuve annule la vérification (le
  // serveur refuserait) ; modifier les autres champs la conserve.
  const editContactField = (setter) => (oldValue, newValue) => {
    setter(newValue);
    if (verificationToken && verifiedAnchors.some((a) => contactsLook(a, oldValue))) {
      setVerificationToken("");
      setVerifiedAnchors([]);
      setFicheProuvee(null);
    }
  };
  const editEmail = editContactField(setEmail);
  const editPhone = editContactField(setPhone);

  const lines = useMemo(() => {
    const out = [];
    offers.forEach((o) => {
      const q = qty[o.id] || 0;
      const c = parseFloat(String(custom[o.id] || "").replace(",", ".")) || 0;
      if (o.kind === "free") {
        if (c > 0) out.push({ offer: o, quantity: 1, amount: c, custom_amount: c });
      } else if (o.kind === "monthly") {
        if (q > 0) {
          const monthly = c > 0 ? c : Number(o.unit_amount || 0);
          out.push({ offer: o, quantity: 1, amount: monthly * (o.months || 12),
                     monthly, custom_amount: c > 0 ? c : null });
        }
      } else if (q > 0) {
        out.push({ offer: o, quantity: q, amount: q * Number(o.unit_amount || 0) });
      }
    });
    return out;
  }, [offers, qty, custom]);

  const oneOff = lines.filter((l) => l.offer.kind !== "monthly")
    .reduce((s, l) => s + l.amount, 0);
  const monthlyTotal = lines.filter((l) => l.offer.kind === "monthly")
    .reduce((s, l) => s + (l.monthly || 0), 0);
  // Ce que la page de paiement prélèvera AUJOURD'HUI : les achats ponctuels
  // + la 1re mensualité de chaque engagement mensuel (le reste suit le
  // rythme mensuel choisi ensuite).
  const dueToday = oneOff + monthlyTotal;
  const monthsMax = Math.max(0, ...lines.filter((l) => l.offer.kind === "monthly")
    .map((l) => l.offer.months || 12));
  const grandTotal = lines.reduce((s, l) => s + l.amount, 0);

  // Groupes d'offres (intertitres) dans l'ordre d'apparition.
  const groups = useMemo(() => {
    const map = new Map();
    offers.forEach((o) => {
      const g = o.group || "Participer";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(o);
    });
    return [...map.entries()];
  }, [offers]);

  // « Une pierre, deux coups » : proposé UNIQUEMENT si le panier ne contient
  // que des offres du 1er groupe (les places) — jamais bloquant.
  const firstGroup = groups[0]?.[0];
  const extras = offers.filter((o) => (o.group || "Participer") !== firstGroup);
  const onlyFirstGroup = lines.length > 0
    && lines.every((l) => (l.offer.group || "Participer") === firstGroup)
    && extras.length > 0;

  const setQ = (id, delta, max) => setQty((s) => {
    const next = Math.max(0, Math.min(max ?? 20, (s[id] || 0) + delta));
    return { ...s, [id]: next };
  });

  const isCompany = donorType === "entreprise";
  // Ce qui manque ENCORE sur la fiche (après le code), mis en avant.
  const valeurDe = { first_name: firstName, last_name: lastName, email, phone, address, postal_code: postalCode, city };
  const manquantsRestants = (ficheProuvee?.missing_fields || [])
    .filter((c) => !String(valeurDe[c] ?? "").trim());
  const surligne = (champ) => (estManquant(manquantsRestants, champ) ? " ring-2 ring-amber-300 bg-amber-50/40" : "");
  // Email figé après le code SEULEMENT s'il a servi à la reconnaissance
  // (c'est lui que le code prouve) ; reconnu par téléphone, il reste libre :
  // un autre email = une autre fiche, jamais celle d'un proche.
  const emailFige = Boolean(verificationToken && ficheProuvee && recognized?.method === "email");
  // Nom de la fiche figé quand le serveur ne l'y remplacerait pas (code reçu
  // par SMS) et que la commande rejoint bien cette fiche (lib/ficheProuvee.js).
  const nomFige = verificationToken && !isCompany
    ? nomsFiges(ficheProuvee, email) : { first_name: false, last_name: false };
  // Paiement direct (décision d'Avi, 30/09/2026) : plus de code pour payer
  // tout de suite. Le code reste demandé pour un abonnement MENSUEL
  // (engagement sur plusieurs mois) ou un don de SOCIÉTÉ — même règle que le
  // serveur (festival_pages_routes.public_order).
  const codeRequis = isCompany || lines.some((l) => l.offer.kind === "monthly");
  const identityValid = email.trim().includes("@")
    && (isCompany ? companyName.trim() : (firstName.trim() && lastName.trim()))
    && (verificationToken || !codeRequis);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const r = await apiClient.post(`/festival-pages/public/${pageId}/order`, {
        lines: lines.map((l) => ({
          offer_id: l.offer.id,
          quantity: l.quantity,
          custom_amount: l.custom_amount ?? null,
        })),
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
        postal_code: postalCode.trim() || null,
        city: city.trim() || null,
        donor_type: donorType,
        company_name: isCompany ? companyName.trim() || null : null,
        siren: isCompany ? siren.trim() || null : null,
        verification_token: verificationToken,
        // Avec le code : le nom de la fiche ne change que s'il a été modifié.
        ...(verificationToken && ficheProuvee
          ? { nom_modifie: nomModifie(ficheProuvee, firstName, lastName) } : {}),
      });
      // Redirection vers la page de paiement Terouma (circuit habituel).
      window.location.href = r.data.pay_url;
    } catch (e) {
      setError(messageErreur(e, "Enregistrement impossible. Réessayez."));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: IVORY }}>
        <PublicSiteHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: GOLD }} />
        </div>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="min-h-screen" style={{ background: IVORY }}>
        <PublicSiteHeader />
        <div className="flex items-center justify-center px-6 min-h-[60vh]">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-8 w-8 mx-auto mb-3" style={{ color: GOLD }} />
          <p className="text-lg" style={{ color: NAVY, fontFamily: "'Space Grotesk', sans-serif" }}>
            {error || "Page introuvable."}
          </p>
        </div>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const goalPct = page.show_goal && page.goal_amount
    ? Math.min(100, Math.round(((stats.collected || 0) / page.goal_amount) * 100))
    : null;

  return (
    <div className="min-h-screen pb-32"
      style={{ background: IVORY, color: NAVY, ...(hexToRgb(page.color) ? { "--accent-rgb": hexToRgb(page.color) } : {}) }}
      data-testid="festival-public-page">
      {/* En-tête SITE (demande d'Avi, réf. AlloDons) : le lien de fête porte
          la navigation Terouma + le menu hamburger — on se croit sur le site. */}
      <PublicSiteHeader />
      <div ref={topRef} />

      {/* ── Fil d'étapes — empilé SOUS l'en-tête site (lui aussi collant) ── */}
      <div className="sticky top-16 md:top-[72px] z-30 backdrop-blur"
        style={{ background: "rgba(251,248,241,0.92)", borderBottom: "1px solid rgba(var(--accent-rgb, 184,150,46),0.22)" }}>
        <div className="max-w-lg mx-auto px-5 py-2.5 flex items-center gap-2">
          {["Participation", "Vos infos", "Paiement"].map((label, i) => {
            const n = i + 1;
            const activeIdx = step <= 2 ? 1 : 2;   // le paiement (3) est l'écran suivant
            const current = n === activeIdx;
            const done = n < activeIdx;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5">
                  <span className="h-5 w-5 rounded-full text-[10px] font-semibold inline-flex items-center justify-center"
                    style={done || current
                      ? { background: GOLD, color: "#fff" }
                      : { background: "rgba(13,27,51,0.07)", color: "rgba(13,27,51,0.45)" }}>
                    {done ? <Check className="h-3 w-3" /> : n}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.14em]"
                    style={{ color: current ? NAVY : "rgba(13,27,51,0.4)" }}>{label}</span>
                </div>
                {n < 3 && <span className="flex-1 h-px" style={{ background: "rgba(var(--accent-rgb, 184,150,46),0.25)" }} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ═════════ ÉTAPE 1 — les offres ═════════ */}
      {step === 1 && (
        <>
          {/* En-tête */}
          <header className="relative">
            {page.header_image && (
              <div className="h-52 sm:h-64 w-full overflow-hidden">
                <img src={page.header_image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0"
                  style={{ background: `linear-gradient(to bottom, rgba(251,248,241,0.05), ${IVORY} 96%)` }} />
              </div>
            )}
            <div className={`max-w-lg mx-auto px-6 text-center ${page.header_image ? "-mt-16 relative" : "pt-10"}`}>
              {/* Logo du Beth Habad — médaillon au-dessus du titre */}
              {page.logo_image && (
                <div className="mx-auto mb-3 h-32 w-32 sm:h-36 sm:w-36 rounded-full overflow-hidden"
                  style={{ border: `2.5px solid ${GOLD}`, background: "#fff",
                           boxShadow: "0 10px 26px -14px rgba(13,27,51,0.5)" }}>
                  {/* object-CONTAIN, jamais cover : un logo carré (texte en
                      bord d'image) était zoomé-rogné par le cercle — retour
                      d'Avi du 03/09. Le logo tient désormais en entier. */}
                  <img src={page.logo_image} alt={org?.name || "Logo"}
                    className="w-full h-full object-contain p-1.5" />
                </div>
              )}
              <p className="text-[11px] uppercase tracking-[0.28em] mb-2" style={{ color: GOLD }}>
                {org?.name}
              </p>
              <h1 className="text-4xl sm:text-5xl mb-1"
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 300, color: NAVY }}>
                {page.title}
              </h1>
              {page.subtitle && (
                <p className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "rgba(13,27,51,0.5)" }}>
                  {page.subtitle}
                </p>
              )}
            </div>
          </header>

          <div className="max-w-lg mx-auto px-5 pt-7">
            {/* Le mot du rav */}
            {page.intro && (
              <div className="rounded-2xl px-5 py-4 mb-5"
                style={{ background: "#fff", border: "1px solid rgba(var(--accent-rgb, 184,150,46),0.25)",
                         boxShadow: "0 8px 24px -18px rgba(13,27,51,0.35)" }}>
                <p className="text-[15px] leading-relaxed" style={{ color: "rgba(13,27,51,0.85)", fontStyle: "italic" }}>
                  « {page.intro} »
                </p>
                {page.signature && (
                  <p className="text-xs mt-2.5 font-medium" style={{ color: GOLD }}>— {page.signature}</p>
                )}
              </div>
            )}

            {/* Participants (par défaut) ou objectif (si la syna en veut un) */}
            {goalPct !== null ? (
              <div className="mb-6">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-lg font-semibold">{eur(stats.collected)}</span>
                  <span className="text-xs" style={{ color: "rgba(13,27,51,0.5)" }}>
                    objectif {eur(page.goal_amount)}
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(13,27,51,0.08)" }}>
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${goalPct}%`, background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})` }} />
                </div>
              </div>
            ) : page.show_participants && stats.participants > 0 ? (
              <div className="flex items-center justify-center gap-2 mb-6 text-sm"
                style={{ color: "rgba(13,27,51,0.6)" }}>
                <Users className="h-4 w-4" style={{ color: GOLD }} />
                <span><strong style={{ color: NAVY }}>{stats.participants}</strong> {stats.participants > 1 ? "familles ont déjà participé" : "famille a déjà participé"}</span>
              </div>
            ) : null}

            {/* Aucune offre encore en vente (prix pas encore fixés) */}
            {groups.length === 0 && (
              <div className="rounded-2xl px-6 py-8 mb-5 text-center"
                style={{ background: "#fff", border: "1px solid rgba(var(--accent-rgb, 184,150,46),0.22)" }}
                data-testid="festival-no-offers">
                <p className="text-[15px]" style={{ color: NAVY }}>Les participations ouvrent très bientôt.</p>
                <p className="text-xs mt-1.5" style={{ color: "rgba(13,27,51,0.55)" }}>Revenez dans quelques jours.</p>
              </div>
            )}

            {/* Les offres, par groupe */}
            {groups.map(([groupName, groupOffers]) => (
              <section key={groupName} className="mb-5">
                <div className="rounded-2xl overflow-hidden"
                  style={{ background: "#fff", border: "1px solid rgba(var(--accent-rgb, 184,150,46),0.22)",
                           boxShadow: "0 10px 30px -22px rgba(13,27,51,0.4)" }}>
                  <div className="px-5 pt-4 pb-2">
                    <h2 className="text-[15px] font-semibold inline-flex items-center gap-2" style={{ color: GOLD }}>
                      {groupOffers[0]?.icon && <span>{groupOffers[0].icon}</span>}
                      {groupName}
                    </h2>
                    {groupOffers[0]?.description && (
                      <p className="text-xs mt-0.5" style={{ color: "rgba(13,27,51,0.5)" }}>
                        {groupOffers[0].description}
                      </p>
                    )}
                  </div>
                  {groupOffers.map((o) => (
                    <div key={o.id} className="px-5 py-3.5 flex items-center gap-3"
                      style={{ borderTop: "1px solid rgba(13,27,51,0.06)" }}
                      data-testid={`offer-${o.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px]" style={{ color: NAVY }}>{o.label}</p>
                        {o.kind === "monthly" ? (
                          <p className="text-sm font-semibold" style={{ color: GOLD }}>
                            {eur(o.unit_amount)} <span className="text-xs font-normal">/ mois · {o.months || 12} mois</span>
                          </p>
                        ) : o.kind === "free" ? (
                          <p className="text-xs" style={{ color: "rgba(13,27,51,0.5)" }}>
                            Facultatif — s'ajoute à votre sélection
                          </p>
                        ) : (
                          <p className="text-sm font-semibold" style={{ color: GOLD }}>{eur(o.unit_amount)}</p>
                        )}
                      </div>

                      {o.kind === "free" ? (
                        <div className="relative w-32">
                          <input
                            inputMode="decimal"
                            value={custom[o.id] || ""}
                            onChange={(e) => setCustom((s) => ({ ...s, [o.id]: e.target.value }))}
                            placeholder="0"
                            data-testid={`offer-free-${o.id}`}
                            className="w-full text-right pr-7 pl-3 py-2 rounded-xl text-sm outline-none"
                            style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: IVORY, color: NAVY }}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: GOLD }}>€</span>
                        </div>
                      ) : o.kind === "monthly" ? (
                        /* Un abonnement s'ACTIVE (pas de quantité) — un seul
                           geste, le montant s'ajuste ensuite en dessous. */
                        <button
                          onClick={() => setQty((s) => ({ ...s, [o.id]: s[o.id] ? 0 : 1 }))}
                          data-testid={`monthly-toggle-${o.id}`}
                          className="h-10 px-4 rounded-full text-sm font-medium transition-all shrink-0"
                          style={(qty[o.id] || 0) > 0
                            ? { background: GOLD, color: "#fff" }
                            : { border: `1.5px solid ${GOLD}`, color: GOLD, background: "transparent" }}
                        >
                          {(qty[o.id] || 0) > 0 ? <><Check className="h-3.5 w-3.5 inline mr-1" />Activée</> : "Activer"}
                        </button>
                      ) : (
                        <div className="inline-flex items-center rounded-full overflow-hidden shrink-0"
                          style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)" }}>
                          <button onClick={() => setQ(o.id, -1, o.max_qty)}
                            aria-label={`Retirer ${o.label}`}
                            data-testid={`minus-${o.id}`}
                            className="h-9 w-9 inline-flex items-center justify-center transition-colors"
                            style={{ color: NAVY }}>
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold tabular-nums">{qty[o.id] || 0}</span>
                          <button onClick={() => setQ(o.id, +1, o.max_qty)}
                            aria-label={`Ajouter ${o.label}`}
                            data-testid={`plus-${o.id}`}
                            className="h-9 w-9 inline-flex items-center justify-center text-white transition-colors"
                            style={{ background: GOLD }}>
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Montant libre d'une offre mensuelle (ex. Beraha) */}
                  {groupOffers.filter((o) => o.kind === "monthly" && o.allow_custom_amount && (qty[o.id] || 0) > 0).map((o) => {
                    const monthly = parseFloat(String(custom[o.id] || "").replace(",", ".")) || Number(o.unit_amount || 0);
                    return (
                      <div key={`custom-${o.id}`} className="px-5 py-3" style={{ borderTop: "1px solid rgba(13,27,51,0.06)", background: "rgba(var(--accent-rgb, 184,150,46),0.05)" }}>
                        <div className="flex items-center gap-3">
                          <span className="text-sm flex-1" style={{ color: "rgba(13,27,51,0.7)" }}>
                            Je préfère donner par mois :
                          </span>
                          <div className="relative w-28">
                            <input
                              inputMode="decimal"
                              value={custom[o.id] || ""}
                              onChange={(e) => setCustom((s) => ({ ...s, [o.id]: e.target.value }))}
                              placeholder={String(o.unit_amount || "")}
                              data-testid={`monthly-custom-${o.id}`}
                              className="w-full text-right pr-7 pl-3 py-2 rounded-xl text-sm outline-none"
                              style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "#fff", color: NAVY }}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: GOLD }}>€</span>
                          </div>
                        </div>
                        <p className="text-xs mt-1.5" style={{ color: "rgba(13,27,51,0.55)" }}>
                          ✓ Votre beraha : <strong>{eur(monthly)} par mois pendant {o.months || 12} mois</strong>
                          {" "}(soit {eur(monthly * (o.months || 12))} sur l'année). Laissez {eur(o.unit_amount)} ou choisissez votre montant.
                        </p>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            <p className="text-center text-[11px] mb-2" style={{ color: "rgba(13,27,51,0.45)" }}>
              Reçu fiscal Cerfa envoyé automatiquement par email · Paiement sécurisé
            </p>
          </div>
        </>
      )}

      {/* ═════════ ÉTAPE 2 — une pierre, deux coups ═════════ */}
      {step === 2 && (
        <div className="max-w-lg mx-auto px-5 pt-8">
          <h2 className="text-2xl mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 300 }}>
            Pendant que vous y êtes…
          </h2>
          <p className="text-sm mb-6" style={{ color: "rgba(13,27,51,0.55)" }}>
            Beaucoup de fidèles en profitent pour ajouter&nbsp;:
          </p>
          {extras.map((o) => {
            const added = o.kind === "free"
              ? !!(parseFloat(String(custom[o.id] || "")) > 0)
              : (qty[o.id] || 0) > 0;
            return (
              <div key={o.id} className="rounded-2xl px-5 py-4 mb-3 flex items-center gap-3"
                style={{ background: "#fff", border: `1px solid ${added ? GOLD : "rgba(var(--accent-rgb, 184,150,46),0.22)"}` }}>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px]">{o.icon ? `${o.icon} ` : ""}{o.label}</p>
                  <p className="text-sm font-semibold" style={{ color: GOLD }}>
                    {o.kind === "monthly" ? `${eur(o.unit_amount)} / mois` : o.kind === "free" ? "Montant libre" : eur(o.unit_amount)}
                  </p>
                  {o.description && (
                    <p className="text-xs mt-0.5" style={{ color: "rgba(13,27,51,0.5)" }}>{o.description}</p>
                  )}
                </div>
                {o.kind === "free" ? (
                  <div className="relative w-28">
                    <input
                      inputMode="decimal"
                      value={custom[o.id] || ""}
                      onChange={(e) => setCustom((s) => ({ ...s, [o.id]: e.target.value }))}
                      placeholder="0"
                      className="w-full text-right pr-7 pl-3 py-2 rounded-xl text-sm outline-none"
                      style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: IVORY }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: GOLD }}>€</span>
                  </div>
                ) : (
                  <button
                    onClick={() => setQ(o.id, added ? -1 : +1, o.max_qty)}
                    data-testid={`extra-${o.id}`}
                    className="h-10 px-4 rounded-full text-sm font-medium transition-all shrink-0"
                    style={added
                      ? { background: GOLD, color: "#fff" }
                      : { border: `1.5px solid ${GOLD}`, color: GOLD, background: "transparent" }}
                  >
                    {added ? <><Check className="h-3.5 w-3.5 inline mr-1" />Ajouté</> : "+ Ajouter"}
                  </button>
                )}
              </div>
            );
          })}
          <button onClick={() => { setPrevStep(2); setStep(3); }}
            data-testid="skip-extras"
            className="w-full text-center text-sm py-3 mt-1"
            style={{ color: "rgba(13,27,51,0.5)" }}>
            Continuer sans ajout →
          </button>
        </div>
      )}

      {/* ═════════ ÉTAPE 3 — identité ═════════ */}
      {step === 3 && (
        <div className="max-w-lg mx-auto px-5 pt-8">
          <h2 className="text-2xl mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 300 }}>
            Vos informations
          </h2>
          <p className="text-sm mb-5" style={{ color: "rgba(13,27,51,0.55)" }}>
            {identityStage === "gate"
              ? "Votre email ou votre téléphone suffit — si vous êtes déjà venu, vos informations reviendront toutes seules."
              : identityStage === "known"
                ? (codeRequis
                  ? "Une dernière vérification et vos informations reviennent toutes seules."
                  : "Payez tout de suite, ou recevez un code pour retrouver et corriger vos informations.")
                : recognized
                  ? "Vérifiez vos informations — elles servent pour le reçu Cerfa."
                  : "Bienvenue ! Complétez vos informations — elles servent pour le reçu Cerfa."}
          </p>

          {/* ── Porte d'entrée : UN champ, email ou téléphone ── */}
          {identityStage === "gate" && (
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-[0.14em] mb-1.5" style={{ color: "rgba(13,27,51,0.5)" }}>
                Email ou téléphone
              </label>
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="vous@email.com ou 06 12 34 56 78"
                data-testid="festival-contact"
                className="w-full px-4 py-3 rounded-xl text-[15px] outline-none"
                style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "#fff" }}
              />
              <p className="text-xs mt-2 flex items-center gap-1.5" style={{ color: "rgba(13,27,51,0.5)" }}>
                {checking
                  ? (<><Loader2 className="h-3 w-3 animate-spin" /> Nous vous cherchons…</>)
                  : gateContact && !gateIsEmail && !gateIsPhone
                    ? "Entrez un email complet (vous@email.com) ou un numéro à 10 chiffres."
                    : "Si vous avez déjà donné ici, nous vous reconnaîtrons."}
              </p>
            </div>
          )}

          {/* ── Reconnu : « ✓ On vous connaît déjà », sans jamais le nom. Le
              code est facultatif pour payer (paiement direct), obligatoire
              pour un abonnement mensuel ou une société. ── */}
          {identityStage === "known" && (
            <div className="mb-4">
              <OnVousConnait
                methode={recognized?.method}
                onRemplir={!codeRequis && !codeOuvert ? () => setCodeOuvert(true) : undefined}
                // Reconnu par l'email : la commande rejoint ce dossier, reçu au
                // nom qui y figure (le nom tapé sans code n'y est pas écrit).
                rattachement={recognized?.method === "email" && !codeRequis ? "don" : undefined}
                aide={codeRequis
                  ? (isCompany
                    ? "Don au nom d'une société : confirmez votre identité."
                    : "Abonnement mensuel : confirmez votre identité (engagement sur plusieurs mois).")
                  : "Avec le code, vos informations (téléphone, adresse) reviennent et vous pouvez les corriger. Sans code, vous les saisissez et payez tout de suite."}
              >
                {(codeRequis || codeOuvert) && (
                  <IdentityVerification
                    organizationId={org?.id}
                    email={recognized?.method === "email" ? recognized.contact : ""}
                    phone={recognized?.method === "phone" ? recognized.contact : ""}
                    recognized={recognized}
                    onVerified={onVerifiedIdentity}
                    context="donate"
                  />
                )}
              </OnVousConnait>
              <div className="flex flex-col items-start gap-2 mt-3">
                <button type="button" onClick={resetIdentity}
                  data-testid="festival-not-me"
                  className="text-xs underline underline-offset-2"
                  style={{ color: "rgba(13,27,51,0.55)" }}>
                  ← Ce n'est pas moi / je me suis trompé de contact
                </button>
                {!codeRequis && (
                  <button type="button"
                    onClick={() => {
                      // On garde le contact (le reçu part à la bonne adresse),
                      // sans rien pré-remplir : la fiche reste cachée sans code.
                      setRecognized(null);
                      setCodeOuvert(false);
                      setIdentityStage("form");
                    }}
                    data-testid="festival-skip-code"
                    className="text-sm font-semibold underline underline-offset-2"
                    style={{ color: NAVY }}>
                    Continuer sans code — je saisis mes informations
                  </button>
                )}
                {(codeRequis || codeOuvert) && (
                  <button type="button"
                    onClick={() => {
                      if (recognized?.method === "email") setEmail("");
                      setRecognized(null);
                      setCodeOuvert(false);
                      setIdentityStage("form");
                    }}
                    data-testid="festival-no-access"
                    className="text-xs underline underline-offset-2"
                    style={{ color: "rgba(13,27,51,0.55)" }}>
                    Je n'ai plus accès à ces contacts — continuer comme nouveau donateur
                  </button>
                )}
              </div>
            </div>
          )}

          {identityStage === "form" && (<>
          <label className="block text-xs uppercase tracking-[0.14em] mb-1.5" style={{ color: "rgba(13,27,51,0.5)" }}>
            Adresse email (pour le reçu)
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => editEmail(email, e.target.value)}
            // Après le code, figé s'il a servi à la reconnaissance (voir emailFige).
            readOnly={emailFige}
            placeholder="vous@email.com"
            data-testid="festival-email"
            className={`w-full px-4 py-3 rounded-xl text-[15px] outline-none mb-2${surligne("email")}`}
            style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: emailFige ? "var(--trm-bg)" : "#fff" }}
          />

          {/* Particulier / Société — même distinction que le reste de Terouma */}
          <div className="flex rounded-xl overflow-hidden mb-3" style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)" }}>
            {[{ v: "particulier", l: "Particulier" }, { v: "entreprise", l: "Société" }].map((t) => (
              <button key={t.v} onClick={() => setDonorType(t.v)}
                data-testid={`festival-type-${t.v}`}
                className="flex-1 py-2.5 text-sm font-medium transition-colors"
                style={donorType === t.v
                  ? { background: NAVY, color: "#fff" }
                  : { background: "#fff", color: NAVY }}>
                {t.l}
              </button>
            ))}
          </div>

          {isCompany && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <input value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Nom de l'entreprise *"
                className="px-4 py-3 rounded-xl text-[15px] outline-none"
                style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "#fff" }} />
              <input value={siren} onChange={(e) => setSiren(e.target.value)}
                placeholder="SIREN (facultatif)"
                className="px-4 py-3 rounded-xl text-[15px] outline-none"
                style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "#fff" }} />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input value={firstName} onChange={(e) => !nomFige.first_name && setFirstName(e.target.value)}
              readOnly={nomFige.first_name}
              placeholder={isCompany ? "Prénom du représentant" : "Prénom *"}
              data-testid="festival-firstname"
              className={`px-4 py-3 rounded-xl text-[15px] outline-none${surligne("first_name")}`}
              style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: nomFige.first_name ? "var(--trm-bg)" : "#fff" }} />
            <input value={lastName} onChange={(e) => !nomFige.last_name && setLastName(e.target.value)}
              readOnly={nomFige.last_name}
              placeholder={isCompany ? "Nom du représentant" : "Nom *"}
              data-testid="festival-lastname"
              className={`px-4 py-3 rounded-xl text-[15px] outline-none${surligne("last_name")}`}
              style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: nomFige.last_name ? "var(--trm-bg)" : "#fff" }} />
            {(nomFige.first_name || nomFige.last_name) && (
              <p className="sm:col-span-2 -mt-1 text-xs leading-relaxed" data-testid="nom-fige"
                style={{ color: "var(--trm-muted)" }}>
                Nom enregistré sur votre dossier : le reçu fiscal sera établi à ce nom.
                Pour un reçu à un autre nom, indiquez votre propre email.
              </p>
            )}
          </div>
          <input value={phone} onChange={(e) => editPhone(phone, e.target.value)}
            placeholder="Téléphone (facultatif)"
            data-testid="festival-phone"
            className={`w-full px-4 py-3 rounded-xl text-[15px] outline-none mb-3${surligne("phone")}`}
            style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "#fff" }} />
          <div className="mb-4">
            <AddressAutocomplete
              value={address}
              onChange={setAddress}
              onSelect={({ address: a, postalCode: pc, city: c }) => { setAddress(a); setPostalCode(pc); setCity(c); }}
              placeholder={isCompany ? "Adresse du siège (pour le reçu)" : "Adresse (recommandée pour le reçu)"}
              testid="festival-address"
              inputClassName={`w-full px-4 py-3 rounded-xl text-[15px] outline-none${isCompany ? "" : surligne("address")}`}
            />
            {/* Après le code : code postal et ville de la fiche, modifiables
                (sinon remplis par le choix d'une adresse proposée). */}
            {verificationToken && ficheProuvee && !isCompany && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="Code postal" inputMode="numeric" autoComplete="postal-code"
                  data-testid="festival-postal-code"
                  className={`px-4 py-3 rounded-xl text-[15px] outline-none${surligne("postal_code")}`}
                  style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "var(--trm-surface)" }} />
                <input value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="Ville" autoComplete="address-level2"
                  data-testid="festival-city"
                  className={`px-4 py-3 rounded-xl text-[15px] outline-none${surligne("city")}`}
                  style={{ border: "1.5px solid rgba(var(--accent-rgb, 184,150,46),0.35)", background: "var(--trm-surface)" }} />
              </div>
            )}
          </div>

          {/* Vérification d'identité — circuit Terouma habituel */}
          {email.trim().includes("@") && !verificationToken && codeRequis && (
            <div className="mb-4">
              <p className="text-xs mb-2" style={{ color: "rgba(13,27,51,0.6)" }}>
                {isCompany
                  ? "Don au nom d'une société : confirmez votre identité."
                  : "Abonnement mensuel : confirmez votre identité (engagement sur plusieurs mois)."}
              </p>
              <IdentityVerification
                organizationId={org?.id}
                email={email.trim()}
                phone={phone.trim()}
                firstName={firstName}
                onVerified={onVerifiedIdentity}
                context="donate"
              />
            </div>
          )}
          {verificationToken && (
            <div className="rounded-xl px-4 py-3 mb-4 text-sm w-full"
              style={{ background: "rgba(16,185,129,0.08)", color: "#047857" }}>
              <p className="inline-flex items-center gap-2 font-medium">
                <ShieldCheck className="h-4 w-4" /> Identité vérifiée
              </p>
              <p className="text-xs mt-1" style={{ color: "rgba(4,120,87,0.85)" }}>
                {recognized
                  ? "Vos informations connues ont été remplies ci-dessus — corrigez ou complétez-les : elles seront enregistrées sur votre fiche et servent pour le reçu Cerfa."
                  : "Vous pouvez maintenant valider — vos informations servent pour le reçu Cerfa."}
              </p>
            </div>
          )}
          {/* Après le code : ce qui manque encore sur la fiche, mis en avant. */}
          {verificationToken && phrasesChampsManquants(manquantsRestants).length > 0 && (
            <ul className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-4 space-y-0.5 text-xs leading-relaxed text-amber-800"
              role="status" data-testid="champs-manquants">
              {phrasesChampsManquants(manquantsRestants).map((ph) => <li key={ph}>{ph}</li>)}
            </ul>
          )}
          {/* Toujours visible (retour d'Avi : impossible de revenir en arrière
              après un numéro mal tapé). Nouveau venu → seul le contact est
              vidé ; identité reconnue/vérifiée → remise à zéro complète. */}
          <button type="button" onClick={backToGate}
            data-testid="festival-restart-identity"
            className="text-xs underline underline-offset-2 mb-4"
            style={{ color: "rgba(13,27,51,0.45)" }}>
            ← Recommencer avec un autre email ou téléphone
          </button>
          </>)}

          {/* Récapitulatif */}
          <div className="rounded-2xl px-5 py-4 mb-4" style={{ background: "#fff", border: "1px solid rgba(var(--accent-rgb, 184,150,46),0.25)" }}>
            <p className="text-[10px] uppercase tracking-[0.16em] mb-2.5" style={{ color: GOLD }}>Votre participation</p>
            {lines.map((l) => (
              <div key={l.offer.id} className="flex items-center justify-between text-sm py-1">
                <span style={{ color: "rgba(13,27,51,0.75)" }}>
                  {l.offer.label}{l.offer.kind === "quantity" && l.quantity > 1 ? ` × ${l.quantity}` : ""}
                  {l.offer.kind === "monthly" ? ` (${eur(l.monthly)} × ${l.offer.months || 12} mois)` : ""}
                </span>
                <span className="tabular-nums font-medium">{eur(l.amount)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between mt-2.5 pt-2.5 text-[15px] font-semibold"
              style={{ borderTop: "1px solid rgba(13,27,51,0.08)" }}>
              <span>{monthlyTotal > 0 ? "À régler aujourd'hui" : "Total"}</span>
              <span className="tabular-nums">{eur(dueToday)}</span>
            </div>
            {monthlyTotal > 0 && (
              <p className="text-xs mt-2 leading-relaxed" style={{ color: "rgba(13,27,51,0.6)" }}>
                Puis <strong>{eur(monthlyTotal)} par mois pendant {Math.max(monthsMax - 1, 0)} mois</strong>
                {" "}(engagement total {eur(grandTotal)}). À l'étape suivante, vous choisirez :
                tout régler en une seule fois, payer chaque mois vous-même, changer le
                rythme, ou activer le prélèvement automatique — limité à ces
                {" "}{monthsMax} mensualités, résiliable à tout moment.
              </p>
            )}
          </div>

          {!identityValid && !error && (
            <p className="text-xs text-center mb-3" style={{ color: "rgba(13,27,51,0.5)" }}>
              {identityStage === "gate"
                ? "Entrez votre email ou votre téléphone pour continuer."
                : identityStage === "known"
                  ? (codeRequis
                    ? "Vérifiez votre identité ci-dessus pour continuer."
                    : "Choisissez ci-dessus : remplir vos informations avec un code, ou continuer sans code.")
                  : !email.trim().includes("@")
                    ? "Renseignez votre email pour continuer."
                    : !verificationToken && codeRequis
                      ? "Dernière étape : confirmez votre identité ci-dessus."
                      : "Complétez votre nom pour continuer."}
            </p>
          )}
          {error && (
            <div className="rounded-xl px-4 py-3 mb-3 text-sm"
              style={{ background: "rgba(185,28,28,0.06)", color: "#991B1B" }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* ── Barre du bas : total + action ── */}
      <div className="fixed bottom-0 inset-x-0 z-40"
        style={{ background: "rgba(251,248,241,0.97)", borderTop: "1px solid rgba(var(--accent-rgb, 184,150,46),0.28)",
                 boxShadow: "0 -12px 30px -20px rgba(13,27,51,0.4)",
                 paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="max-w-lg mx-auto px-5 pt-3 flex items-center gap-3">
          {step > 1 && (
            <button onClick={() => setStep(step === 3 ? prevStep : 1)}
              aria-label="Retour"
              className="h-12 w-12 rounded-full inline-flex items-center justify-center shrink-0"
              style={{ border: "1.5px solid rgba(13,27,51,0.15)", color: NAVY }}>
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "rgba(13,27,51,0.45)" }}>
              {monthlyTotal > 0 ? "À régler aujourd'hui" : "Total"}
            </p>
            <p className="text-xl font-semibold tabular-nums leading-tight" style={{ color: NAVY }}>
              {eur(dueToday)}
            </p>
            {monthlyTotal > 0 && (
              <p className="text-[11px] leading-tight" style={{ color: GOLD }}>
                puis {eur(monthlyTotal)}/mois × {Math.max(monthsMax - 1, 0)}
              </p>
            )}
          </div>
          <button
            disabled={lines.length === 0 || (step === 3 && (!identityValid || submitting))}
            onClick={() => {
              if (step === 1) { setPrevStep(1); setStep(onlyFirstGroup ? 2 : 3); }
              else if (step === 2) { setPrevStep(2); setStep(3); }
              else submit();
            }}
            data-testid="festival-cta"
            className="h-12 px-6 rounded-full inline-flex items-center gap-2 text-white text-sm font-semibold shrink-0 transition-all disabled:opacity-40"
            style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)` }}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" />
              : step === 3 ? <Lock className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            {step === 3 ? "Aller au paiement" : "Continuer"}
          </button>
        </div>
      </div>

      {/* Pied de page */}
      <footer className="max-w-lg mx-auto px-5 pt-8 pb-4 text-center">
        {/* Logo cliquable (retour d'Avi) : il ramène sur le site Terouma. */}
        <Link to="/" aria-label="Découvrir Terouma"
          className="inline-flex items-center gap-2 opacity-45 hover:opacity-90 transition-opacity">
          <TeroumaLogo size={22} variant="wordmark" textColor={NAVY} />
        </Link>
        <p className="text-[10px] mt-1.5 inline-flex items-center gap-1 justify-center w-full"
          style={{ color: "rgba(13,27,51,0.4)" }}>
          <Heart className="h-3 w-3" /> Paiement sécurisé · reçu Cerfa automatique
        </p>
      </footer>
    </div>
  );
}
